import React, { useState } from 'react';
import { Button } from '../ui/button';
import SEO from '../SEO/SEO';
import { X } from 'lucide-react';
import './AhamMoments.css';
import { motion } from 'framer-motion';
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import api from '../../services/api';
import { useEffect } from 'react';

const AhamMoments = () => {
  const navigate = useNavigate();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [banner, setBanner] = useState(null);
  const [workSettings, setWorkSettings] = useState(null);
  const [workImages, setWorkImages] = useState([]);
  const [celebrationSettings, setCelebrationSettings] = useState(null);
  const [celebrationImages, setCelebrationImages] = useState([]);
  const [growthSettings, setGrowthSettings] = useState(null);
  const [growthImages, setGrowthImages] = useState([]);
  const [ctaSettings, setCtaSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const baseUrl = backendUrl.endsWith('/api') ? backendUrl.replace('/api', '') : backendUrl;

  // Fetch banner and section data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bannerRes, workSettingsRes, workImagesRes, celebrationSettingsRes, celebrationImagesRes, growthSettingsRes, growthImagesRes, ctaRes] = await Promise.all([
          api.get('/admin/banners/active?page_type=gallery'),
          api.get('/admin/gallery/work/settings'),
          api.get('/admin/gallery/work/images'),
          api.get('/admin/gallery/celebration/settings'),
          api.get('/admin/gallery/celebration/images'),
          api.get('/admin/gallery/growth/settings'),
          api.get('/admin/gallery/growth/images'),
          api.get('/admin/gallery/cta/settings')
        ]);

        const galleryBanner = (bannerRes.data.data || []).find(
          b => b.page_type === 'gallery' && b.is_active
        );
        if (galleryBanner) setBanner(galleryBanner);

        if (workSettingsRes.data.success) setWorkSettings(workSettingsRes.data.data);
        if (workImagesRes.data.success) {
          setWorkImages(workImagesRes.data.data.filter(img => img.is_active));
        }

        if (celebrationSettingsRes.data.success) setCelebrationSettings(celebrationSettingsRes.data.data);
        if (celebrationImagesRes.data.success) {
          setCelebrationImages(celebrationImagesRes.data.data.filter(img => img.is_active));
        }

        if (growthSettingsRes.data.success) setGrowthSettings(growthSettingsRes.data.data);
        if (growthImagesRes.data.success) {
          setGrowthImages(growthImagesRes.data.data.filter(img => img.is_active));
        }

        if (ctaRes.data.success) setCtaSettings(ctaRes.data.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);



  const openLightbox = (image) => {
    setSelectedImage(image);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const GallerySection = ({ title, description, images, theme }) => (
    <section className={`gallery-section theme-${theme}`}>
      <div className="section-container">
        <div className="section-header centered">
          <h2 className="section-title">{title}</h2>
          <p className="section-description">{description}</p>
        </div>

        <div className="gallery-grid">
          {images.map((image, index) => (
            <div
              key={index}
              className="gallery-item"
              onClick={() => openLightbox(image)}
            >
              <div className="gallery-image-wrapper">
                <img
                  src={image.url.startsWith('http') ? image.url : `${baseUrl}${image.url.startsWith('/') ? '' : '/'}${image.url}`}
                  alt={image.alt}
                  className="gallery-image"
                />
                <div className="gallery-overlay">
                  <p className="gallery-caption">{image.caption}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  return (
    <div className="aham-moments">

      <SEO
        title="Aham Moments | Home Loan Success Stories & Journeys"
        description="Discover real Aham Moments—true home loan success stories from self-employed and first-time buyers. See how Aham Housing Finance helped them own a home.!"
        keywords="Aham Moments, Aham Housing Finance moments, Aham events, Aham celebrations, life at Aham, Aham team moments, Aham gallery, Aham Housing Finance culture, employee engagement Aham, branch inauguration Aham, corporate celebrations Aham, team events Aham, workplace culture Aham, Aham family moments, Aham office celebrations, Aham employee activities, Aham event highlights, Aham community moments, Aham Housing Finance gallery, memorable moments at Aham"
        canonical="https://ahamhfc.com/aham-moments-home-loan-success-stories"
        ogTitle="Aham Moments | Home Loan Success Stories & Journeys"
        ogDescription="Discover real Aham Moments—true home loan success stories from self-employed and first-time buyers. See how Aham Housing Finance helped them own a home."
      />

      {/* Hero Banner */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${banner && banner.image_url ? (banner.image_url.startsWith('http') ? banner.image_url : banner.image_url.startsWith('/') ? banner.image_url : `/${banner.image_url}`) : '/img/Banners/Gallery.webp'}')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
              {banner?.title || 'Aham Moments'}
            </h1>
            <p className="text-lg md:text-3xl text-white/90 font-medium max-w-4xl leading-relaxed mb-8">
              {banner?.subtitle || 'Celebrating the people, the energy, and the everyday stories that make Aham feel like home.'}
            </p>

            {/* Breadcrumb Navigation */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="flex items-center space-x-2"
            >
              <Link
                to="/"
                className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
              >
                Home
              </Link>
              <ChevronRightIcon className="h-4 w-4 text-white/60" />
              <span className="text-yellow-300 text-sm font-semibold">Aham Moments</span>
            </motion.div>

            {/* Hidden SEO Image for Alt Tag requirement */}
            <img
              src={banner?.image_url || '/img/Banners/Gallery.webp'}
              alt="Happy Aham Moments home loan customers celebrating their new home with Aham Housing Finance"
              className="hidden"
            />



          </motion.div>
        </div>
      </section>

      {/* Work in Motion Section */}
      <GallerySection
        title={workSettings?.section_title || 'Where Ideas Come Alive'}
        description={workSettings?.section_description || 'Work at Aham is dynamic, collaborative, and built on trust. Every conversation, every sketch, every whiteboard moment — it\'s all part of building something meaningful together.'}
        images={workImages.map(img => ({
          url: img.image_url,
          alt: img.alt_text,
          caption: img.caption
        }))}
        theme="work"
      />

      {/* Celebrations Section */}
      <GallerySection
        title={celebrationSettings?.section_title || 'We Celebrate Together'}
        description={celebrationSettings?.section_description || 'We believe joy is fuel. Festivals, birthdays, milestones, and spontaneous laughter — these are the threads that shape our culture and remind us that work can be joyful too.'}
        images={celebrationImages.map(img => ({
          url: img.image_url,
          alt: img.alt_text,
          caption: img.caption
        }))}
        theme="celebration"
      />

      {/* Growth Section */}
      <GallerySection
        title={growthSettings?.section_title || 'Growing, Learning, Becoming'}
        description={growthSettings?.section_description || 'Aham is not just a workplace. It\'s a journey. We learn from each other, challenge each other, and rise together — building not just careers, but confidence and character.'}
        images={growthImages.map(img => ({
          url: img.image_url,
          alt: img.alt_text,
          caption: img.caption
        }))}
        theme="growth"
      />

      {/* CTA Section */}
      <section className="moments-cta">
        <div className="cta-container">
          <h2 className="cta-heading">{ctaSettings?.title || 'Want to be part of these moments?'}</h2>
          <p className="cta-text">{ctaSettings?.description || 'Join our team and create your own story at Aham.'}</p>
          <div className="cta-buttons">
            <Button
              variant="outline"
              className="cta-secondary"
              onClick={() => navigate(ctaSettings?.button_link || '/careers')}>
              {ctaSettings?.button_text || 'Contact'}
            </Button>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && selectedImage && (
        <div className="lightbox" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox}>
            <X size={32} />
          </button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedImage.url.startsWith('http') ? selectedImage.url : `${baseUrl}${selectedImage.url.startsWith('/') ? '' : '/'}${selectedImage.url}`}
              alt={selectedImage.alt}
            />
            <p className="lightbox-caption">{selectedImage.caption}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AhamMoments;